<template>
  <div class="user-avatar-popover">
    <button @click="togglePopover" class="avatar-button" id="avatar-button">
      <IonAvatar class="nav-avatar">
        <img v-if="isAuthenticated && userName" :src="avatarUrl" :alt="$t('nav.userAvatar')" />
        <IonIcon v-else :icon="personCircleOutline" class="default-avatar-icon" />
      </IonAvatar>
    </button>

    <IonPopover
      :is-open="isPopoverOpen"
      :trigger="isMobileWeb ? undefined : 'avatar-button'"
      :show-backdrop="true"
      :backdrop-dismiss="true"
      :side="isMobileWeb ? undefined : 'bottom'"
      :alignment="isMobileWeb ? undefined : 'start'"
      class="user-popover"
      :class="{ 'mobile-web': isMobileWeb }"
      @didDismiss="closePopover"
    >
      <IonContent class="popover-content ion-no-background">
        <div :class="cardClasses">
          <!-- User Info Section -->
          <section class="user-info-section">
            <UserInfoCard
              :is-authenticated="isAuthenticated"
              :user-name="userName"
              :user-email="userEmail"
              :avatar-url="avatarUrl"
              :is-mobile-web="isMobileWeb"
              @click="goToProfile"
            />
          </section>

          <!-- Action Cards Section -->
          <section class="action-cards-section">
            <LanguageCard
              ref="languageCardRef"
              :locales="availableLocales"
              :current-locale="currentLocale"
              :current-flag="currentLocaleFlag"
              :current-name="getLocaleName(currentLocale)"
              :is-hovered="false"
              :is-mobile-web="isMobileWeb"
              @select="handleLanguageChange"
              @hover="(h) => (hoverCard = h ? 'language' : null)"
            />

            <!-- <ActionCard
              :title="$t('nav.settings')"
              :is-hovered="hoverCard === 'settings'"
              :is-mobile-web="isMobileWeb"
              @click="goToSettings"
              @hover="(h) => (hoverCard = h ? 'settings' : null)"
            >
              <template #icon>
                <IconRenderer name="settings-icon" size="32px" />
              </template>
            </ActionCard> -->

            <ActionCard
              v-if="userType === USER_TYPE.STUDENT"
              :title="''"
              :is-hovered="false"
              :is-mobile-web="isMobileWeb"
              @click="navigateToOldWard"
              @hover="(h) => (hoverCard = h ? 'settings' : null)"
              class="old-ward-card"
            >
              <p class="ward-button-title">Ward V.1</p>
            </ActionCard>

            <ActionCard
              title=""
              variant="no-hover"
              :is-hovered="false"
              :is-mobile-web="isMobileWeb"
              @hover="(h) => (hoverCard = h ? 'style' : null)"
            >
              <ThemeUnifiedSwitch mode="style" size="10" @theme-toggled="handleThemeToggle" />
            </ActionCard>

            <ActionCard
              v-if="isAuthenticated"
              :title="$t('common.logout')"
              :icon="logOutOutline"
              variant="danger"
              :is-hovered="false"
              :is-mobile-web="isMobileWeb"
              @click="handleLogout"
              @hover="(h) => (hoverCard = h ? 'logout' : null)"
            />
          </section>
        </div>
      </IonContent>
    </IonPopover>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { IonAvatar, IonButton, IonContent, IonIcon, IonPopover } from '@ionic/vue';
import { logOutOutline, personCircleOutline } from 'ionicons/icons';

import ThemeUnifiedSwitch from '@/common/components/ui/switch/ThemeUnifiedSwitch.vue';
import { useDeviceDetection } from '@/common/composables/useDeviceDetection';
import { useLocale } from '@/common/composables/useLocale';
import { useTheming } from '@/common/composables/useTheming';
import { useUser } from '@/common/composables/useUser';
import { USER_TYPE } from '@/common/constants';
import { notificationService } from '@/common/services/notification/notificationService';
import { useUserStore } from '@/common/stores/userStore';
import { authService } from '@/features/common/auth/services/authService';
import { logger } from '@/common/services/config';

import ActionCard from './ActionCard.vue';
import LanguageCard from './LanguageCard.vue';
import UserInfoCard from './UserInfoCard.vue';

// Emits
const emit = defineEmits<{
  themeToggled: [];
}>();

// Composables
const router = useRouter();
const userStore = useUserStore();
const { isAuthenticated, userName, userEmail, userType } = useUser();
const { currentLocale, availableLocales, changeLocale, getLocaleName } = useLocale();
const { isMobileWeb } = useDeviceDetection();
const { themeMode, toggleStyleMode } = useTheming();

// Refs
const languageCardRef = ref<InstanceType<typeof LanguageCard> | null>(null);

// State
const isPopoverOpen = ref(false);
const hoverCard = ref<string | null>(null);

// Computed
const popoverClasses = computed(() => ['user-popover', { 'mobile-web': isMobileWeb.value }]);
const cardClasses = computed(() => ['popover-card', { 'mobile-web': isMobileWeb.value }]);
const avatarUrl = computed(
  () => userStore.user?.photo ?? 'https://ionicframework.com/docs/img/demos/avatar.svg'
);

const currentLocaleFlag = computed(() => {
  const locale = availableLocales.value.find((l) => l.code === currentLocale.value);
  return locale?.flag ?? '🌐';
});

// Popover methods
const togglePopover = () => {
  if (isPopoverOpen.value) {
    closePopover();
  } else {
    isPopoverOpen.value = true;
  }
};

const closePopover = async () => {
  isPopoverOpen.value = false;
  languageCardRef.value?.closeOptions();
  hoverCard.value = null;
  await nextTick();
};

// Handlers
const handleLanguageChange = (localeCode: string) => {
  changeLocale(localeCode);
  notificationService.info('notifications.languageChanged', {
    language: getLocaleName(localeCode),
  });
};

const handleThemeToggle = () => {
  toggleStyleMode();
  emit('themeToggled');
  const newStyle = themeMode.value === 'modern' ? 'Classique' : 'Moderne';
  notificationService.info('notifications.themeChanged', { theme: `Style ${newStyle}` });
};

const goToSettings = async () => {
  router.push({ name: 'Settings' });
  await closePopover();
};

const goToProfile = async () => {
  if (userStore.userType === USER_TYPE.STUDENT) {
    router.push({ name: 'MyProfile' });
    await closePopover();
  }
};

const handleLogout = async () => {
  await closePopover();
  try {
    await authService.signOut();
    notificationService.success('notifications.logoutSuccess');
  } catch (error) {
    logger.error('Logout failed:', error);
  }
};

// Lifecycle
onMounted(async () => {
  if (isAuthenticated.value && !userStore.user) {
    try {
      await userStore.getUser();
    } catch (error) {
      logger.error('Failed to load user:', error);
    }
  }
});

watch(isAuthenticated, async (authenticated) => {
  if (!authenticated && isPopoverOpen.value) {
    await closePopover();
  }
});

const navigateToOldWard = () => {
  window.open('https://ward.em-normandie.fr/', '_blank');
};
</script>

<style scoped>
.user-avatar-popover {
  position: relative;
}

.avatar-button {
  width: 60px;
  height: 60px;
  background-color: var(--ion-background-color-layout);
}

.nav-avatar {
  width: 42px;
  height: 42px;
  transition: border-color 0.2s ease;
}

.nav-avatar:hover {
  border-color: var(--ion-color-primary);
}

.default-avatar-icon {
  font-size: 32px;
  color: var(--ion-text-color);
}

/* Popover Container */
.user-popover {
  --width: 520px;
  --background: transparent;
  --box-shadow: none;
  --backdrop-opacity: 0.8;
  --offset-y: 10px;
}

.user-popover.mobile-web {
  --width: calc(100vw - 90px);
  --max-width: 100%;
  --offset-y: 0px;
}

.popover-content {
  --background: transparent;
}

/* Main Card Layout */
.popover-card {
  display: flex;
  gap: 24px;
  padding: 14px;
  background: var(--ion-background-color);
  border: 2px solid var(--ion-input-border-color);
  border-radius: 3vmin;
}

.popover-card.mobile-web {
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

/* User Info Section */
.user-info-section {
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
}

.popover-card.mobile-web .user-info-section {
  flex: 0 0 auto;
  width: 100%;
}

/* Action Cards Section */
.action-cards-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.popover-card.mobile-web .action-cards-section {
  width: 100%;
  gap: 12px;
}

.old-ward-card {
  background: linear-gradient(
    120deg,
    rgba(var(--ion-color-tertiary-rgb)) 0%,
    rgba(var(--ion-color-secondary-rgb)) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
}

.old-ward-card:hover {
  background: linear-gradient(
    120deg,
    rgba(var(--ion-color-tertiary-rgb)) 0%,
    rgba(var(--ion-color-secondary-rgb), 0.8) 100%
  );
  filter: brightness(1.1);
}

.ward-button-title {
  font-size: 30px;
  font-weight: 600;
  color: var(--ion-text-color-contrast);
}
</style>

<style>
ion-popover.user-popover {
  --background: transparent;
  --box-shadow: none;
}

ion-popover.user-popover::part(content) {
  background: var(--ion-background-color) !important;
  border-radius: 3vmin;
  overflow: hidden;
}

ion-popover.user-popover.mobile-web {
  --offset-x: 0px;
  --offset-y: 0px;
  align-items: center;
  justify-content: center;
}

ion-popover.user-popover.mobile-web::part(content) {
  position: relative;
  top: 30%;
  left: 50%;
  transform: translate(-50%, 30%);
}

ion-popover.user-popover::part(arrow) {
  display: none;
}

ion-popover.user-popover ion-content {
  --background: transparent;
  --ion-background-color: transparent;
}

ion-popover.user-popover ion-content::part(background) {
  background: transparent !important;
}

ion-popover.user-popover ion-content::part(scroll) {
  background: transparent;
}
</style>
